import { createReducer, on } from '@ngrx/store'
import { ProjectActions } from './action-types'
import _ from 'lodash'

const initState: IProjectState = {
    schema: {
        areaList: [],
        clientRequestList: [],
        concernedSubjectList: [],
        subjectKnList: [],
        subjectLpisList: [],
        customerContractList: [],
        downloadOwnershipOverview: false,
        lvList: [],
        parList: [],
        project: {
            allowLiquibase: true,
            allowActualization: true,
            blockUsers: false,
            projectActualization: {
                dataActualTo: null,
                applicationDataActualTo: null,
                applicationDataBeforeActualTo: null,
                applicationLpisDataActualTo: null,
                lpisDataActualTo: null,
                runAddNewData: false,
                runActualization: false
            },
            projectDefault: {
                defaultCreateIndividualDocuments: true,
                mapLat: 49.75,
                mapLon: 15.75,
                mapZoom: 4,
                paymentsFromEconomicYear: false,
                useTaxFromRentedAcreage: true,
            },
            projectOption: {
                allowExportOwned: false,
                allowRepriceContracts: true,
                allowTaxes: true,
                allowInflationIncrease: true,
                contractPaymentStart: new Date().getFullYear() + '-01-01',
                contractPaymentIncomingStart: new Date().getFullYear() + '-01-01',
                nonCadastralData: true,
                remoteSystemType: 'NONE',
                allowIfrsExport: false,
                allowSignature: false
            },
            projectRequest: {
                updatePeriodMonths: null,
                ownershipOverviewDate: null,
                regularRequestYear: null,
                regularRequestMonth: null,
                regularRequestDataTo: null,
                extraRequestYear: null,
                extraRequestMonth: null,
                extraRequestDataTo: null
            }
        }
    },
    anyJpv: false,
}

export const projectReducer = createReducer(
    initState,
    on(ProjectActions.resetSchema, () => {
        return {...initState}
    }),
    on(ProjectActions.setSchemaProperty, (state, props) => {
        const copy = {...state.schema}

        copy[props.property] = props.value

        return {...state, schema: copy}
    }),
    on(ProjectActions.setProjectProperty, (state, props) => {
        const copy = {...state.schema.project}

        copy[props.property] = props.value

        return {...state, schema: {...state.schema, project: copy}}
    }),
    on(ProjectActions.setProjectActualizationProperty, (state, props) => {
        const copy = {...state.schema.project.projectActualization}

        copy[props.property] = props.value

        return {...state, schema: {...state.schema, project: {...state.schema.project, projectActualization: copy}}}
    }),
    on(ProjectActions.setProjectDefaultProperty, (state, props) => {
        const copy = {...state.schema.project.projectDefault}

        copy[props.property] = props.value

        return {...state, schema: {...state.schema, project: {...state.schema.project, projectDefault: copy}}}
    }),
    on(ProjectActions.setProjectOptionProperty, (state, props) => {
        const copy = {...state.schema.project.projectOption}

        copy[props.property] = props.value

        return {...state, schema: {...state.schema, project: {...state.schema.project, projectOption: copy}}}
    }),
    on(ProjectActions.setProjectRequestProperty, (state, props) => {
        const copy = {...state.schema.project.projectRequest}

        copy[props.property] = props.value

        return {...state, schema: {...state.schema, project: {...state.schema.project, projectRequest: copy}}}
    }),
    on(ProjectActions.setSchema, (state, props) => {
        return {...state, schema: props.schema}
    }),
    on(ProjectActions.setSchemaJpv, (state, props) => {
        return {...state, anyJpv: props.anyJpv}
    }),
    on(ProjectActions.removeArea, (state, props) => {
        const areaListCopy = [...state.schema.areaList]
        const indexToDelete = areaListCopy.findIndex((a: any) => a.katuzeKod === props.area.katuzeKod && a.jpv === props.area.jpv)
        areaListCopy.splice(indexToDelete, 1)

        return {...state, schema: {...state.schema, areaList: areaListCopy}}
    }),
    on(ProjectActions.addConcernedSubject, (state, props) => {
        const subjectListCopy = [...state.schema.concernedSubjectList]
        subjectListCopy.push(props.subject)

        return {...state, schema: {...state.schema, concernedSubjectList: subjectListCopy}}
    }),
    on(ProjectActions.removeConcernedSubject, (state, props) => {
        const subjectListCopy = [...state.schema.concernedSubjectList]
        const indexToDelete = subjectListCopy.findIndex((c: any) => c.id === props.subject.id)
        subjectListCopy.splice(indexToDelete, 1)

        return {...state, schema: {...state.schema, concernedSubjectList: subjectListCopy}}
    }),
    on(ProjectActions.addSubjectKn, (state, props) => {
        const subjectKnListCopy = [...state.schema.subjectKnList]
        subjectKnListCopy.push(props.subject)

        return {...state, schema: {...state.schema, subjectKnList: subjectKnListCopy}}
    }),
    on(ProjectActions.addSubjectLpis, (state, props) => {
        const subjectLpisListCopy = [...state.schema.subjectLpisList]
        subjectLpisListCopy.push(props.subject)

        return {...state, schema: {...state.schema, subjectLpisList: subjectLpisListCopy}}
    }),
    on(ProjectActions.removeSubjectKn, (state, props) => {
        let subjectKnListCopy = [...state.schema.subjectKnList]
        const indexToDelete = subjectKnListCopy.findIndex((c: any) => c.opsubId === props.subject.opsubId)
        subjectKnListCopy.splice(indexToDelete, 1)

        return {...state, schema: {...state.schema, subjectKnList: subjectKnListCopy}}
    }),
    on(ProjectActions.removeSubjectLpis, (state, props) => {
        const subjectLpisListCopy = [...state.schema.subjectLpisList]
        const indexToDelete = subjectLpisListCopy.findIndex((c: any) => c.lpisUserId === props.subject.lpisUserId)
        subjectLpisListCopy.splice(indexToDelete, 1)

        return {...state, schema: {...state.schema, subjectLpisList: subjectLpisListCopy}}
    }),
    on(ProjectActions.removeLv, (state, props) => {
        const lvListCopy = [...state.schema.lvList]
        const indexToDelete = lvListCopy.findIndex((l: any) => l.lv === props.lv.lv && l.katuzeKod === props.lv.katuzeKod && l.jpv === props.lv.jpv)
        lvListCopy.splice(indexToDelete, 1)

        return {...state, schema: {...state.schema, lvList: lvListCopy}}
    }),
    on(ProjectActions.removePars, (state, props) => {
        const parListCopy = [...state.schema.parList]

        return {...state, schema: {...state.schema,
                parList: _.differenceWith(parListCopy, props.pars, (added: any, par: any) => added.parId === par.parId)}}
    }),
    on(ProjectActions.updateAnyJpv, (state) => {
        const arr =    [
            ...state.schema.areaList,
            ...state.schema.concernedSubjectList,
            ...state.schema.subjectKnList,
            ...state.schema.subjectLpisList,
            ...state.schema.lvList,
            ...state.schema.parList,
        ]

        const anyJpv = arr.filter(item => item.jpv)
                        .map(item => item.jpv)
                        .length > 0

        return {...state, anyJpv: anyJpv}

    }),
    on(ProjectActions.addPars, (state, props) => {
        const actualPars = [...state.schema.parList]

        // filter added pars to eleminate duplicates
        const parsToAdd = _.differenceWith(props.pars, actualPars, (added: any, actual: any) => added.parId === actual.parId)

        return {...state, schema: {...state.schema, parList: [...actualPars, ...parsToAdd]}}
    }),
)

export interface IProjectState {
    schema: IProjectSchema;
    anyJpv: boolean;
}

export interface IProjectSchema {
    areaList: any[];
    concernedSubjectList: any[];
    clientRequestList: any[];
    subjectKnList: any[];
    subjectLpisList: any[];
    customerContractList: any[];
    downloadOwnershipOverview: boolean;
    lvList: any[];
    parList: any[];
    project: IProjectSchemaProject;
}

export interface IProjectSchemaProject {
    allowLiquibase: boolean;
    allowActualization: boolean;
    blockUsers: boolean;
    projectActualization: {
        dataActualTo: string;
        applicationDataActualTo: string;
        applicationDataBeforeActualTo: string;
        applicationLpisDataActualTo: string;
        lpisDataActualTo: string;
        runAddNewData: boolean;
        runActualization: boolean;
    };
    projectDefault: {
        defaultCreateIndividualDocuments: boolean;
        mapLat: number;
        mapLon: number;
        mapZoom: number;
        paymentsFromEconomicYear: boolean;
        useTaxFromRentedAcreage: boolean;
    };
    projectOption: {
        allowExportOwned: boolean;
        allowRepriceContracts: boolean;
        allowTaxes: boolean;
        allowInflationIncrease: boolean;
        contractPaymentStart: string;
        contractPaymentIncomingStart: string;
        nonCadastralData: boolean;
        remoteSystemType: string;
        allowIfrsExport: boolean;
        allowSignature: boolean;
    };
    projectRequest: {
        updatePeriodMonths: number;
        ownershipOverviewDate: string;
        regularRequestYear: number;
        regularRequestMonth: number;
        regularRequestDataTo: string;
        extraRequestYear: number;
        extraRequestMonth: number;
        extraRequestDataTo: string;
    };
}